'use client';

/**
 * ComposerIntentCard — submit-time intent toggle.
 *
 * `useCartStore.activeIntent` is the late-bound tag the cart store
 * carries; this card surfaces it as a 4-way picker. Switching is
 * non-destructive — items remain, only the validator and payload-
 * builder swap. RailCard chrome to match the Insights surface.
 *
 * Mirrors the legacy drawer's intent strip (`cart-drawer.tsx::CART_INTENTS`),
 * authored as parallel rail UI; the legacy strip stays in place
 * during cohabitation.
 *
 * @see klyde_edge/composition/cards/composer.py — composer_intent spec
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain procurement
 */

import { ChevronDown, ChevronUp, ClipboardList, Pencil, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useCardExport } from '@/lib/design/card-export';
import { useCartStore, type IntentType } from '@/lib/stores/cart-store';
import { useMcpActions } from '@/lib/hooks/use-mcp-actions';
import { useAffordance, useCardProps, useComposerLabel } from '@/lib/stores/total-matrix-store';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { RailCard } from '@/components/copilot/rail-insights/rail-card';
import { CostPlanPicker, type CostPlanPickerPick } from '@/components/copilot/rail-insights/cost-plan-surface';

// Intents that are STATES the composer can arrive in (loaded from an
// entity — explorer kebab, RFI panel) rather than picker options. The
// stale-intent guard must not bounce these even though the flag does
// not OFFER them; they render as edit-mode chrome instead.
const ARRIVABLE_INTENTS: ReadonlySet<string> = new Set(['rfq_edit']);

const INTENT_OPTIONS: ReadonlyArray<{
  key: IntentType;
  labelKey: string;
  icon: string;
  hintKey: string;
}> = [
  { key: 'rfq', labelKey: 'intent_rfq_label', icon: '📋', hintKey: 'intent_rfq_hint' },
  { key: 'rfq_edit', labelKey: 'intent_rfq_edit_label', icon: '✏️', hintKey: 'intent_rfq_edit_hint' },
  { key: 'po', labelKey: 'intent_po_label', icon: '✨', hintKey: 'intent_po_hint' },
  { key: 'mcp', labelKey: 'intent_mcp_label', icon: '📊', hintKey: 'intent_mcp_hint' },
];

function IntentButton({ opt, active, onClick, bodyClass }: {
  opt: (typeof INTENT_OPTIONS)[number];
  active: boolean;
  onClick: () => void;
  bodyClass: string;
}) {
  const label = useComposerLabel(opt.labelKey);
  const hint = useComposerLabel(opt.hintKey);
  const selectTestId = useAffordance('composer.intent.select', { key: opt.key });
  return (
    <button
      data-testid={selectTestId}
      onClick={onClick}
      title={hint}
      className={cn(
        bodyClass,
        'flex items-center gap-1.5 px-2 py-1.5 rounded-md border transition-colors text-left',
        active
          ? 'border-primary bg-primary/10 text-primary font-medium'
          : 'border-border bg-background hover:border-primary/40 hover:bg-muted/50',
      )}
    >
      <span className="text-base leading-none">{opt.icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}

export function ComposerIntentCard() {  const activeIntent = useCartStore((s) => s.activeIntent);
  const setIntent = useCartStore((s) => s.setIntent);
  const editingMcpSid = useCartStore((s) => s.editingMcpSid);
  const title = useComposerLabel('compose_title');
  // 'mcp' is a real picker option too (unlike 'rfq_edit', which is
  // ARRIVABLE-only) — it's only an edit-arrival state once a plan is
  // actually loaded into it. See the real incident this guards against:
  // an editingMcpSid left set from a prior "Edit" click silently
  // survived into an unrelated Discovery session and merged 104 new
  // lines onto that old plan — see docs/wip.md.
  const isMcpEditArrival = activeIntent === 'mcp' && !!editingMcpSid;

  // BFF-declared feature flag (composition as data): the card spec's
  // default_props names which intents the picker offers. Absent prop
  // → all intents (fail-open keeps dev matrices working).
  const cardProps = useCardProps('composer_intent');
  const enabledIntents = useMemo(() => {
    const raw = cardProps.enabled_intents;
    return Array.isArray(raw) && raw.length > 0
      ? INTENT_OPTIONS.filter((o) => (raw as string[]).includes(o.key))
      : INTENT_OPTIONS;
  }, [cardProps]);

  // A persisted/stale active intent that the flag no longer offers
  // falls back to the first enabled one — EXCEPT arrivable states
  // (rfq_edit), which are entered by loading an entity and must
  // survive; bouncing them would destroy the loaded edit session.
  useEffect(() => {
    if (
      activeIntent &&
      !ARRIVABLE_INTENTS.has(activeIntent) &&
      !isMcpEditArrival &&
      !enabledIntents.some((o) => o.key === activeIntent)
    ) {
      setIntent(enabledIntents[0]?.key ?? 'rfq');
    }
  }, [activeIntent, isMcpEditArrival, enabledIntents, setIntent]);

  // Scene-only export — the composer is cart-store draft state, no query.
  useCardExport(useMemo(() => ({
    label: title || 'Intent',
    content_key: 'composer_intent',
    surface: 'composer' as const,
    status: 'loaded' as const,
    query_key: null,
    text: `Intent: ${activeIntent ?? '—'}`,
    json: { active_intent: activeIntent ?? null },
  }), [title, activeIntent]));

  // Edit mode replaces the picker: mid-edit intent switching would
  // orphan the loaded RFQ; the one legal exit is discard (or submit).
  if (activeIntent === 'rfq_edit') {
    return (
      <RailCard title={title} icon={ClipboardList}>
        <EditModeBanner kind="rfq" />
      </RailCard>
    );
  }

  if (isMcpEditArrival) {
    return (
      <RailCard title={title} icon={ClipboardList}>
        <EditModeBanner kind="mcp" />
      </RailCard>
    );
  }

  return (
    <RailCard title={title} icon={ClipboardList}>
      {/* Unified "Send to" header — consolidates the old 2x2 intent
          grid + the separate "target an existing plan" link into one
          collapsed/expand control (see this file's task docstring).
          Picking any option here still just flips activeIntent /
          targetMcpForAppend — RFQ/PO/MCP's own downstream field sets
          (dates, vendors, name/revision, …) are untouched. */}
      <SendToControl
        activeIntent={activeIntent}
        enabledIntents={enabledIntents}
        setIntent={setIntent}
      />
    </RailCard>
  );
}

/**
 * SendToControl — the unified "Send to" header (step 2). Collapsed:
 * "Send to: {current destination} ▾". Expanded: the same four
 * destination options the old 2x2 grid offered, plus the existing-
 * plan search folded in as a distinct fifth/fourth action (unchanged
 * `TargetExistingPlanSection` picker, just mounted here instead of as
 * its own standalone link). Picking any option collapses the control
 * back to its summary row.
 *
 * Deliberately does NOT attempt to also collapse/represent the
 * `EditModeBanner` arrival states (rfq_edit, mcp edit arrival) — those
 * already render their OWN compact summary row with Discard/detach
 * (see ComposerIntentCard's early returns above); this control only
 * ever mounts for the "nothing arrived yet, still choosing" case.
 */
function SendToControl({
  activeIntent, enabledIntents, setIntent,
}: {
  activeIntent: IntentType;
  enabledIntents: typeof INTENT_OPTIONS;
  setIntent: (intent: IntentType) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  // Existing-plan picker used to be its own nested toggle (a text link
  // that opened a second, separately-collapsed box) — two clicks deep,
  // capped at a cramped h-64. Folded directly into this panel instead:
  // one click from the grid swaps straight to the picker, at a height
  // that actually uses the panel's real estate.
  const [showPicker, setShowPicker] = useState(false);
  const sendToLabel = useComposerLabel('send_to_label');
  const current = INTENT_OPTIONS.find((o) => o.key === activeIntent);
  const currentLabel = useComposerLabel(current?.labelKey ?? 'intent_rfq_label');
  const toggleTestId = useAffordance('composer.intent.send_to_toggle');
  const existingPlanLabel = useComposerLabel('target_plan_toggle');
  const cancelLabel = useComposerLabel('target_plan_cancel');
  const { targetMcpForAppend } = useMcpActions();

  const handlePickIntent = (key: IntentType) => {
    setIntent(key);
    setExpanded(false);
  };

  const handlePickPlan = (pick: CostPlanPickerPick) => {
    targetMcpForAppend({
      mcpSid: pick.mcpId,
      projectSid: pick.projectSid,
      projectName: pick.projectName,
      name: pick.mcpName,
    });
    setShowPicker(false);
    setExpanded(false);
  };

  if (!expanded) {
    return (
      <button
        type="button"
        data-testid={toggleTestId}
        onClick={() => setExpanded(true)}
        className={cn(
          text.body,
          'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md border transition-colors text-left',
          'border-border bg-background hover:border-primary/40 hover:bg-muted/50',
        )}
      >
        <span className="truncate">
          {sendToLabel}: <span className="font-medium">{currentLabel}</span>
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      </button>
    );
  }

  if (showPicker) {
    return (
      <div className="rounded-md border border-border overflow-hidden">
        <div className="flex items-center justify-between px-2 py-1.5 border-b border-border bg-muted/30">
          <button
            type="button"
            onClick={() => setShowPicker(false)}
            className={cn(text.muted, 'flex items-center gap-1 text-muted-foreground hover:text-foreground')}
          >
            <ChevronUp className="h-3.5 w-3.5 -rotate-90" />
            {cancelLabel}
          </button>
        </div>
        {/* CostPlanPicker's root is `h-full overflow-y-auto` (built for
            the rail surface's full-height slot) — the whole point of
            folding its own toggle away was to give it real room, so
            this is generous on purpose, not another small box. */}
        <div className="h-[420px]">
          <CostPlanPicker onPin={handlePickPlan} />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-border bg-muted/30">
        <span className={cn(text.body, 'font-medium')}>{sendToLabel}</span>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className={cn(text.muted, 'text-muted-foreground hover:text-foreground')}
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="p-2 space-y-1.5">
        <div className="grid grid-cols-2 gap-1.5">
          {enabledIntents.map((opt) => (
            <IntentButton
              key={opt.key}
              opt={opt}
              active={activeIntent === opt.key}
              onClick={() => handlePickIntent(opt.key)}
              bodyClass={text.body}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className={cn(
            text.body,
            'w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md border transition-colors text-left',
            'border-border bg-background hover:border-primary/40 hover:bg-muted/50',
          )}
        >
          <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <span className="truncate">{existingPlanLabel}</span>
        </button>
      </div>
    </div>
  );
}

/** Edit-mode chrome — an arrivable state (rfq_edit, or mcp with
 *  editingMcpSid set) made visible: what is being edited and the one
 *  exit. Discard clears the whole draft (the lines belonged to the
 *  loaded entity) — this is also the ONLY way editingMcpSid/
 *  editingRfqSid ever return to null outside a successful submit, so
 *  it doubles as "start a new one" now that it's actually visible
 *  instead of silently carrying forward across sessions. */
function EditModeBanner({ kind }: { kind: 'rfq' | 'mcp' }) {
  const projectName = useCartStore((s) => s.projectName);
  const mcpPlanName = useCartStore((s) => s.mcpPlanName);
  const editingMode = useCartStore((s) => s.editingMode);
  const clear = useCartStore((s) => s.clear);
  const detachMcpEditToRfq = useCartStore((s) => s.detachMcpEditToRfq);
  // Lightweight target (targetMcpForAppend, no lines loaded) gets its
  // own banner text — distinct from the full-reload edit session
  // (loadMcpForEdit) so a buyer can tell "appending new items" apart
  // from "editing the whole plan in place."
  const isLightAppend = kind === 'mcp' && editingMode === 'light_append';
  const banner = useComposerLabel(
    isLightAppend ? 'target_plan_banner' : kind === 'mcp' ? 'editing_mcp_banner' : 'editing_rfq_banner',
  );
  const discard = useComposerLabel(kind === 'mcp' ? 'editing_mcp_discard' : 'editing_rfq_discard');
  const toRfq = useComposerLabel('editing_mcp_to_rfq');
  // A plan's own name, not the project's — falls back to project name
  // only if the plan itself somehow has none.
  const subtitle = kind === 'mcp' ? (mcpPlanName || projectName) : projectName;
  return (
    <div className="flex items-center gap-2 rounded-md border border-primary/25 bg-primary/5 px-2.5 py-2">
      <Pencil className="h-3.5 w-3.5 text-primary flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className={cn(text.body, 'font-medium text-primary truncate')}>{banner}</div>
        {subtitle && (
          <div className={cn(text.muted, 'truncate')}>{subtitle}</div>
        )}
      </div>
      {/* mcp only, and not for a lightweight append — detachMcpEditToRfq
          is specifically "I loaded a whole plan to edit, but actually
          want these lines as an RFQ instead." A light_append session
          never loaded a whole plan, so there's nothing to detach FROM;
          offering it here is just clutter in an already-tight row.
          rfq_edit has no equivalent move either — editing an EXISTING
          RFQ document in place isn't the same shape as detaching lines
          toward a new artifact. See detachMcpEditToRfq's docstring for
          why this is safe in the full-reload case (clears editingMcpSid,
          same as Discard, so the original plan is never touched
          afterward — just keeps the lines). */}
      {kind === 'mcp' && !isLightAppend && (
        <button
          type="button"
          onClick={detachMcpEditToRfq}
          title={toRfq}
          className={cn(text.muted, 'flex items-center gap-1 text-primary hover:underline flex-shrink-0')}
        >
          {toRfq}
        </button>
      )}
      <button
        type="button"
        onClick={clear}
        title={discard}
        className={cn(text.muted, 'flex items-center gap-1 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0')}
      >
        <X className="h-3.5 w-3.5" />
        {discard}
      </button>
    </div>
  );
}
